import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useUser } from "../../../Home/UserContext";
import { useNotification } from "../../../../components/Notification/NotificationManager";

import {
  createGroup,
  deleteGroup,
  getGroupsByUser,
  getGroupAdherents,
  updateGroup,
  updateAdherentStatus,
  deleteAdherent,
  generateInvite,
  addAdherentToGroupManually,
  updateGroupAdherent,
  moveAdherentToAnotherGroup,
} from "../../../../services/groupServices";

import {
  addNewAdherent,
  getAdherentById,
  getUserIdByAdherentId,
} from "../../../../services/adherentServices";

import { sendMessage } from "../../../../services/messageServices";
import { sendNotificationToAdmin } from "../../../../services/notificationServices";
import { getRoleFromRoleId } from "../../../../utils/roleHelper";

import {
  DEFAULT_GROUP_FORM,
  DEFAULT_MESSAGE_DRAFT,
  buildGroupsStats,
  getRoleLimits,
  getUserId,
  normalizeAdherentPayload,
  normalizeGroupPayload,
  sortGroups,
  validateAdherentForm,
  normalizeAdherentUpdatePayload,
} from "../utils/groupsUtils";

const DEFAULT_MODALS = {
  create: false,
  edit: false,
  invite: false,
  message: false,
  status: false,
  createAdherent: false,
  editAdherent: false,
};

export function useGroupsPage() {
  const { user } = useUser();
  const { addNotification } = useNotification();

  const notificationRef = useRef(addNotification);
  const hasLoadedOnceRef = useRef(false);

  const [groups, setGroups] = useState([]);
  const [adherentsData, setAdherentsData] = useState({});

  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedAdherent, setSelectedAdherent] = useState(null);
  const [inviteLink, setInviteLink] = useState("");

  const [messageDraft, setMessageDraft] = useState(DEFAULT_MESSAGE_DRAFT);
  const [messageContext, setMessageContext] = useState({
    recipients: [],
    isGroupMessage: false,
    groupName: "",
    adherentName: "",
  });

  const [modals, setModals] = useState(DEFAULT_MODALS);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [savingAction, setSavingAction] = useState("");

  const userId = useMemo(() => getUserId(user), [user]);
  const userRole = useMemo(() => getRoleFromRoleId(user?.role_id), [user]);
  const userLimits = useMemo(() => getRoleLimits(user?.role_id), [user]);

  useEffect(() => {
    notificationRef.current = addNotification;
  }, [addNotification]);

  const stats = useMemo(() => {
    return buildGroupsStats(groups, adherentsData, userLimits);
  }, [groups, adherentsData, userLimits]);

  const openModal = useCallback((name) => {
    setModals((prev) => ({
      ...prev,
      [name]: true,
    }));
  }, []);

  const closeModal = useCallback((name) => {
    setModals((prev) => ({
      ...prev,
      [name]: false,
    }));
  }, []);

  const fetchGroups = useCallback(
    async ({ silent = false } = {}) => {
      const isInitialLoad = !hasLoadedOnceRef.current;

      if (!userId) {
        setGroups([]);
        setAdherentsData({});
        setLoading(false);
        return;
      }

      if (isInitialLoad) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      try {
        const userGroups = await getGroupsByUser(userId);
        const safeGroups = Array.isArray(userGroups) ? userGroups : [];
        const orderedGroups = sortGroups(safeGroups);

        const adherentsEntries = await Promise.all(
          orderedGroups.map(async (group) => {
            try {
              const response = await getGroupAdherents(group.id);
              return [group.id, response?.adherents || []];
            } catch (error) {
              console.error(
                `Erreur lors de la récupération des adhérents du groupe ${group.id} :`,
                error,
              );
              return [group.id, []];
            }
          }),
        );

        setGroups(orderedGroups);
        setAdherentsData(Object.fromEntries(adherentsEntries));

        if (!silent && !isInitialLoad) {
          notificationRef.current?.("Groupes actualisés.", "success");
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des groupes :", error);

        notificationRef.current?.(
          "Erreur lors de la récupération des groupes.",
          "error",
        );
      } finally {
        hasLoadedOnceRef.current = true;
        setLoading(false);
        setRefreshing(false);
      }
    },
    [userId],
  );

  useEffect(() => {
    fetchGroups({ silent: true });
  }, [fetchGroups]);

  const openCreateModal = useCallback(() => {
    if (!stats.canCreateGroup) {
      notificationRef.current?.(
        `Vous avez atteint la limite de ${userLimits.maxGroups} groupe(s) autorisé(s).`,
        "error",
      );
      return;
    }

    openModal("create");
  }, [openModal, stats.canCreateGroup, userLimits.maxGroups]);

  const closeCreateModal = useCallback(() => {
    closeModal("create");
  }, [closeModal]);

  const openEditModal = useCallback(
    (group) => {
      setSelectedGroup(group);
      openModal("edit");
    },
    [openModal],
  );

  const closeEditModal = useCallback(() => {
    closeModal("edit");
    setSelectedGroup(null);
  }, [closeModal]);

  const openCreateAdherentDrawer = useCallback(
    (group) => {
      if (!group?.id) return;

      if (stats.totalAdherents >= userLimits.maxAdherents) {
        notificationRef.current?.(
          `Vous avez atteint la limite de ${userLimits.maxAdherents} judoka(s) autorisé(s).`,
          "error",
        );
        return;
      }

      setSelectedGroup(group);
      openModal("createAdherent");
    },
    [openModal, stats.totalAdherents, userLimits.maxAdherents],
  );

  const closeCreateAdherentDrawer = useCallback(() => {
    closeModal("createAdherent");
    setSelectedGroup(null);
  }, [closeModal]);

  const closeInviteModal = useCallback(() => {
    closeModal("invite");
    setInviteLink("");
    setSelectedGroup(null);
  }, [closeModal]);

  const closeMessageModal = useCallback(() => {
    closeModal("message");
    setMessageDraft(DEFAULT_MESSAGE_DRAFT);
    setMessageContext({
      recipients: [],
      isGroupMessage: false,
      groupName: "",
      adherentName: "",
    });
  }, [closeModal]);

  const closeStatusModal = useCallback(() => {
    closeModal("status");
    setSelectedAdherent(null);
    setSelectedGroup(null);
  }, [closeModal]);

  const handleCreateGroup = useCallback(
    async (form) => {
      if (!userId) {
        notificationRef.current?.("Utilisateur non identifié.", "error");
        return;
      }

      setSavingAction("create-group");

      try {
        const payload = normalizeGroupPayload({
          ...form,
          user_id: userId,
          quota_groupe: 0,
          coach_name: "",
        });

        await createGroup(payload);

        const token = localStorage.getItem("token");
        const message = `Le coach ${user?.prenom || ""} ${
          user?.nom || ""
        } a créé un nouveau groupe : ${payload.nom}`;

        if (token) {
          await sendNotificationToAdmin(message, token);
        }

        notificationRef.current?.("Groupe créé avec succès.", "success");

        closeCreateModal();
        await fetchGroups({ silent: true });
      } catch (error) {
        console.error("Erreur lors de la création du groupe :", error);

        notificationRef.current?.(
          "Une erreur est survenue lors de la création du groupe.",
          "error",
        );
      } finally {
        setSavingAction("");
      }
    },
    [closeCreateModal, fetchGroups, user, userId],
  );

  const handleUpdateGroup = useCallback(
    async (form) => {
      if (!selectedGroup?.id) return;

      setSavingAction("update-group");

      try {
        const payload = normalizeGroupPayload({
          ...form,
          id: selectedGroup.id,
          quota_groupe: selectedGroup.quota_groupe || 0,
          coach_name: selectedGroup.coach_name || "",
        });

        await updateGroup(payload);

        notificationRef.current?.("Groupe mis à jour avec succès.", "success");

        closeEditModal();
        await fetchGroups({ silent: true });
      } catch (error) {
        console.error("Erreur lors de la mise à jour du groupe :", error);

        notificationRef.current?.(
          "Une erreur est survenue lors de la mise à jour du groupe.",
          "error",
        );
      } finally {
        setSavingAction("");
      }
    },
    [closeEditModal, fetchGroups, selectedGroup],
  );

  const handleCreateAdherentInGroup = useCallback(
    async (form) => {
      console.log(
        "[useGroupsPage] handleCreateAdherentInGroup - données reçues",
        {
          form,
          user,
          userId,
          selectedGroup,
          selectedGroupId: selectedGroup?.id,
          stats,
          userLimits,
        },
      );

      if (!userId) {
        notificationRef.current?.("Utilisateur non identifié.", "error");
        return;
      }

      if (!selectedGroup?.id) {
        notificationRef.current?.("Groupe non identifié.", "error");
        return;
      }

      const validationError = validateAdherentForm(form);

      console.log("[useGroupsPage] Validation formulaire adhérent", {
        validationError,
        form,
      });

      if (validationError) {
        notificationRef.current?.(validationError, "error");
        return;
      }

      if (stats.totalAdherents >= userLimits.maxAdherents) {
        notificationRef.current?.(
          `Vous avez atteint la limite de ${userLimits.maxAdherents} judoka(s) autorisé(s).`,
          "error",
        );
        return;
      }

      setSavingAction("create-adherent");

      try {
        const payload = normalizeAdherentPayload(form, userId);
        console.table(payload);

        console.log("[useGroupsPage] Payload envoyé à addNewAdherent", {
          payload,
          userId,
          tokenExists: Boolean(localStorage.getItem("token")),
        });

        const createdAdherent = await addNewAdherent(payload);

        console.log("[useGroupsPage] Réponse addNewAdherent", {
          createdAdherent,
        });

        const createdAdherentId =
          createdAdherent?.id ||
          createdAdherent?.adherent?.id ||
          createdAdherent?.data?.id;

        console.log("[useGroupsPage] ID adhérent créé détecté", {
          createdAdherentId,
          selectedGroupId: selectedGroup.id,
        });

        if (!createdAdherentId) {
          throw new Error("ID adhérent introuvable après création.");
        }

        console.log("[useGroupsPage] Payload envoyé au rattachement groupe", {
          groupId: selectedGroup.id,
          adherentId: createdAdherentId,
          status: "approved",
        });

        await addAdherentToGroupManually(
          selectedGroup.id,
          createdAdherentId,
          "approved",
        );

        const token = localStorage.getItem("token");
        const message = `Le coach ${user?.prenom || ""} ${
          user?.nom || ""
        } a ajouté manuellement ${payload.prenom} ${payload.nom} au groupe "${
          selectedGroup.nom
        }".`;

        if (token) {
          try {
            await sendNotificationToAdmin(message, token);
          } catch (notificationError) {
            console.warn("Notification admin non envoyée :", notificationError);
          }
        }

        notificationRef.current?.(
          "Judoka créé et ajouté au groupe avec succès.",
          "success",
        );

        closeCreateAdherentDrawer();
        await fetchGroups({ silent: true });
      } catch (error) {
        console.error(
          "[useGroupsPage] Erreur lors de la création manuelle de l'adhérent",
          {
            error,
            response: error?.response,
            responseData: error?.response?.data,
            status: error?.response?.status,
            url: error?.config?.url,
            method: error?.config?.method,
            requestData: error?.config?.data,
          },
        );

        notificationRef.current?.(
          error?.response?.data?.message ||
            "Erreur lors de la création du judoka.",
          "error",
        );
      } finally {
        setSavingAction("");
      }
    },
    [
      closeCreateAdherentDrawer,
      fetchGroups,
      selectedGroup,
      stats,
      user,
      userId,
      userLimits,
    ],
  );

  const handleDeleteGroup = useCallback(
    async (group) => {
      if (!group?.id) return;

      const confirmDelete = window.confirm(
        `Voulez-vous vraiment supprimer le groupe "${group.nom}" ?`,
      );

      if (!confirmDelete) return;

      setSavingAction(`delete-group-${group.id}`);

      try {
        await deleteGroup(group.id);

        notificationRef.current?.("Groupe supprimé avec succès.", "success");

        await fetchGroups({ silent: true });
      } catch (error) {
        console.error("Erreur lors de la suppression du groupe :", error);

        notificationRef.current?.(
          "Erreur lors de la suppression du groupe.",
          "error",
        );
      } finally {
        setSavingAction("");
      }
    },
    [fetchGroups],
  );

  const handleInviteGroup = useCallback(
    async (group) => {
      if (!group?.id) return;

      setSavingAction(`invite-group-${group.id}`);
      setSelectedGroup(group);
      setInviteLink("");

      try {
        const response = await generateInvite(group.id);
        setInviteLink(response?.inviteLink || "");
        openModal("invite");
      } catch (error) {
        console.error(
          "Erreur lors de la génération du lien d'invitation :",
          error,
        );

        notificationRef.current?.(
          "Erreur lors de la génération du lien d'invitation.",
          "error",
        );
      } finally {
        setSavingAction("");
      }
    },
    [openModal],
  );

  const resolveRecipientUserIds = useCallback(async (adherentsList = []) => {
    const userIds = await Promise.all(
      adherentsList.map(async (adherent) => {
        try {
          const response = await getUserIdByAdherentId(adherent.id);

          return (
            response?.[0]?.UserAdherent?.user_id ||
            response?.[0]?.user_id ||
            response?.user_id ||
            null
          );
        } catch (error) {
          console.warn(
            `Aucun user_id trouvé pour ${adherent?.prenom || ""} ${
              adherent?.nom || ""
            }`,
            error,
          );

          return null;
        }
      }),
    );

    return Array.from(new Set(userIds.filter(Boolean)));
  }, []);

  const notifyAdherentManagers = useCallback(
    async (adherent, subject, content) => {
      if (!userId || !adherent?.id) return;

      const recipients = await resolveRecipientUserIds([adherent]);

      if (!recipients.length) return;

      await Promise.all(
        recipients.map((recipientId) =>
          sendMessage({
            sender_id: userId,
            recipient_id: recipientId,
            subject,
            content,
          }),
        ),
      );
    },
    [resolveRecipientUserIds, userId],
  );

  const handleOpenStatusModal = useCallback(
    (adherent, group) => {
      setSelectedAdherent(adherent);
      setSelectedGroup(group);
      openModal("status");
    },
    [openModal],
  );

  const handleApproveAdherent = useCallback(async () => {
    if (!selectedAdherent?.id || !selectedGroup?.id) return;

    setSavingAction("approve-adherent");

    try {
      await updateAdherentStatus(
        selectedAdherent.id,
        selectedGroup.id,
        "approved",
      );

      await notifyAdherentManagers(
        selectedAdherent,
        "Demande approuvée",
        `Votre demande pour rejoindre le groupe "${selectedGroup.nom}" a été approuvée.`,
      );

      notificationRef.current?.(
        "Adhérent accepté avec succès et responsable notifié.",
        "success",
      );

      closeStatusModal();
      await fetchGroups({ silent: true });
    } catch (error) {
      console.error("Erreur lors de l'approbation :", error);

      notificationRef.current?.(
        error?.response?.data?.message || "Erreur lors de l'approbation.",
        "error",
      );
    } finally {
      setSavingAction("");
    }
  }, [
    closeStatusModal,
    fetchGroups,
    notifyAdherentManagers,
    selectedAdherent,
    selectedGroup,
  ]);

  const handleRejectAdherent = useCallback(async () => {
    if (!selectedAdherent?.id || !selectedGroup?.id) return;

    setSavingAction("reject-adherent");

    try {
      await updateAdherentStatus(
        selectedAdherent.id,
        selectedGroup.id,
        "rejected",
      );

      await notifyAdherentManagers(
        selectedAdherent,
        "Demande refusée",
        `Votre demande pour rejoindre le groupe "${selectedGroup.nom}" a été refusée. Contactez le coach pour plus d'informations.`,
      );

      notificationRef.current?.(
        "Adhérent refusé avec succès et responsable notifié.",
        "success",
      );

      closeStatusModal();
      await fetchGroups({ silent: true });
    } catch (error) {
      console.error("Erreur lors du refus :", error);

      notificationRef.current?.(
        error?.response?.data?.message || "Erreur lors du refus.",
        "error",
      );
    } finally {
      setSavingAction("");
    }
  }, [
    closeStatusModal,
    fetchGroups,
    notifyAdherentManagers,
    selectedAdherent,
    selectedGroup,
  ]);

  const handleDeleteAdherent = useCallback(async () => {
    if (!selectedAdherent?.id || !selectedGroup?.id) return;

    const confirmDelete = window.confirm(
      `Supprimer ${selectedAdherent.prenom || ""} ${
        selectedAdherent.nom || ""
      } du groupe "${selectedGroup.nom}" ?`,
    );

    if (!confirmDelete) return;

    setSavingAction("delete-adherent");

    try {
      await deleteAdherent(selectedAdherent.id, selectedGroup.id);

      notificationRef.current?.("Adhérent supprimé du groupe.", "success");

      closeStatusModal();
      await fetchGroups({ silent: true });
    } catch (error) {
      console.error("Erreur lors de la suppression de l'adhérent :", error);

      notificationRef.current?.(
        "Erreur lors de la suppression de l'adhérent.",
        "error",
      );
    } finally {
      setSavingAction("");
    }
  }, [closeStatusModal, fetchGroups, selectedAdherent, selectedGroup]);

  const handleOpenMessageModal = useCallback(
    async (adherentsList = [], isGroupMessage = false, group = null) => {
      if (!adherentsList.length) {
        notificationRef.current?.(
          "Aucun adhérent disponible pour envoyer un message.",
          "error",
        );
        return;
      }

      setSavingAction("prepare-message");

      try {
        const recipients = await resolveRecipientUserIds(adherentsList);

        if (!recipients.length) {
          notificationRef.current?.(
            "Aucun destinataire valide trouvé.",
            "error",
          );
          return;
        }

        setMessageContext({
          recipients,
          isGroupMessage,
          groupName: group?.nom || "",
          adherentName: isGroupMessage
            ? ""
            : `${adherentsList[0]?.prenom || ""} ${
                adherentsList[0]?.nom || ""
              }`.trim(),
        });

        setMessageDraft(DEFAULT_MESSAGE_DRAFT);
        openModal("message");
      } catch (error) {
        console.error("Erreur lors de la préparation du message :", error);

        notificationRef.current?.(
          "Erreur lors de la récupération des destinataires.",
          "error",
        );
      } finally {
        setSavingAction("");
      }
    },
    [openModal, resolveRecipientUserIds],
  );

  const handleMessageDraftChange = useCallback((name, value) => {
    setMessageDraft((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleSendMessage = useCallback(async () => {
    if (!userId) {
      notificationRef.current?.("Utilisateur non identifié.", "error");
      return;
    }

    if (!messageDraft.subject.trim() || !messageDraft.content.trim()) {
      notificationRef.current?.(
        "Sujet et contenu requis pour envoyer un message.",
        "error",
      );
      return;
    }

    setSavingAction("send-message");

    try {
      await Promise.all(
        messageContext.recipients.map((recipientId) =>
          sendMessage({
            sender_id: userId,
            recipient_id: recipientId,
            subject: messageDraft.subject.trim(),
            content: messageDraft.content.trim(),
          }),
        ),
      );

      notificationRef.current?.(
        messageContext.isGroupMessage
          ? "Message envoyé aux responsables du groupe."
          : "Message envoyé au responsable de l'adhérent.",
        "success",
      );

      closeMessageModal();
    } catch (error) {
      console.error("Erreur lors de l'envoi du message :", error);

      notificationRef.current?.("Erreur lors de l'envoi du message.", "error");
    } finally {
      setSavingAction("");
    }
  }, [closeMessageModal, messageContext, messageDraft, userId]);

  const openEditAdherentDrawer = useCallback(
    async (adherent, group) => {
      console.log("[useGroupsPage] openEditAdherentDrawer", {
        adherent,
        group,
      });

      const adherentId =
        adherent?.id ||
        adherent?.adherent_id ||
        adherent?.Adherent?.id ||
        adherent?.adherent?.id;

      if (!adherentId || !group?.id) {
        notificationRef.current?.("Adhérent ou groupe non identifié.", "error");
        return;
      }

      setSavingAction(`load-adherent-${adherentId}`);

      try {
        const response = await getAdherentById(adherentId);

        const fullAdherent =
          response?.adherent || response?.data || response || adherent;

        console.log("[useGroupsPage] Adhérent complet pour édition", {
          adherentId,
          response,
          fullAdherent,
        });

        setSelectedAdherent({
          ...adherent,
          ...fullAdherent,
          id: adherentId,
          status:
            adherent?.status || adherent?.AdherentGroup?.status || "approved",
        });

        setSelectedGroup(group);
        openModal("editAdherent");
      } catch (error) {
        console.error(
          "[useGroupsPage] Erreur chargement adhérent complet :",
          error,
        );

        notificationRef.current?.(
          "Impossible de charger les informations complètes du judoka.",
          "error",
        );
      } finally {
        setSavingAction("");
      }
    },
    [openModal],
  );

  const closeEditAdherentDrawer = useCallback(() => {
    closeModal("editAdherent");
    setSelectedAdherent(null);
    setSelectedGroup(null);
  }, [closeModal]);

  const handleUpdateAdherentInGroup = useCallback(
    async (form) => {
      if (!selectedAdherent?.id) {
        notificationRef.current?.("Adhérent non identifié.", "error");
        return;
      }

      if (!selectedGroup?.id) {
        notificationRef.current?.("Groupe non identifié.", "error");
        return;
      }

      const validationError = validateAdherentForm(form);

      if (validationError) {
        notificationRef.current?.(validationError, "error");
        return;
      }

      const currentGroupId = Number(selectedGroup.id);
      const targetGroupId = Number(form.target_group_id || selectedGroup.id);

      if (!targetGroupId) {
        notificationRef.current?.("Groupe cible non identifié.", "error");
        return;
      }

      setSavingAction("update-adherent");

      try {
        const payload = normalizeAdherentUpdatePayload(form);

        await updateGroupAdherent(currentGroupId, selectedAdherent.id, payload);

        if (targetGroupId !== currentGroupId) {
          await moveAdherentToAnotherGroup(
            currentGroupId,
            targetGroupId,
            selectedAdherent.id,
            selectedAdherent.status || "approved",
          );
        }

        notificationRef.current?.(
          targetGroupId !== currentGroupId
            ? "Judoka modifié et déplacé vers le nouveau groupe."
            : "Judoka modifié avec succès.",
          "success",
        );

        closeEditAdherentDrawer();
        await fetchGroups({ silent: true });
      } catch (error) {
        console.error("[useGroupsPage] Erreur modification adhérent :", {
          error,
          response: error?.response,
          responseData: error?.response?.data,
          status: error?.response?.status,
          url: error?.config?.url,
          method: error?.config?.method,
          requestData: error?.config?.data,
        });

        notificationRef.current?.(
          error?.response?.data?.message ||
            "Erreur lors de la modification du judoka.",
          "error",
        );
      } finally {
        setSavingAction("");
      }
    },
    [closeEditAdherentDrawer, fetchGroups, selectedAdherent, selectedGroup],
  );

  return {
    user,
    userRole,
    userLimits,
    groups,
    adherentsData,
    selectedGroup,
    selectedAdherent,
    inviteLink,
    messageDraft,
    messageContext,
    modals,
    loading,
    refreshing,
    savingAction,
    stats,
    fetchGroups,
    openCreateModal,
    openEditModal,
    openCreateAdherentDrawer,
    closeCreateModal,
    closeEditModal,
    closeCreateAdherentDrawer,
    closeInviteModal,
    closeMessageModal,
    closeStatusModal,
    handleCreateGroup,
    handleUpdateGroup,
    handleCreateAdherentInGroup,
    handleDeleteGroup,
    handleInviteGroup,
    handleOpenStatusModal,
    handleApproveAdherent,
    handleRejectAdherent,
    handleDeleteAdherent,
    handleOpenMessageModal,
    handleMessageDraftChange,
    handleSendMessage,
    openEditAdherentDrawer,
    closeEditAdherentDrawer,
    handleUpdateAdherentInGroup,
  };
}
